/**
 * Realtime Context
 * 
 * Provides connection state and event subscription for the unified realtime provider.
 * Consumed by components that need to know about sync status or react to order changes.
 */

import { createContext, useContext } from "react";

export type ConnectionState = 'connecting' | 'live' | 'degraded';

export interface OrderChangePayload {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  /** The new order data (null for DELETE) */
  newOrder: Record<string, unknown> | null;
  /** The old order data (partial, available for UPDATE/DELETE) */
  oldOrder: Partial<Record<string, unknown>> | null;
}

export type OrderChangeCallback = (payload: OrderChangePayload) => void;

export interface RealtimeContextValue {
  /**
   * Current connection state:
   * - 'connecting': Initial state, establishing connection
   * - 'live': Successfully subscribed to realtime channel
   * - 'degraded': Connection lost or failed, using fallback polling
   */
  connectionState: ConnectionState;

  /**
   * Subscribe to order change events from the unified realtime channel.
   * Returns an unsubscribe function.
   */
  subscribeToChanges: (callback: OrderChangeCallback) => () => void;
}

// No-op subscriber for default context
const noopSubscribe = () => () => {};

export const RealtimeContext = createContext<RealtimeContextValue>({
  connectionState: 'connecting',
  subscribeToChanges: noopSubscribe,
});

export function useRealtimeContext() {
  return useContext(RealtimeContext);
}
